/* eslint-disable no-useless-escape */
/* eslint-disable react/no-danger */
import getConfig from 'next/config';
import React from 'react';
import startOfDay from 'date-fns/startOfDay';
import isAfter from 'date-fns/isAfter';
import isObject from 'lodash/isObject';

import { convertToZurichTz, getDateZurich } from './date';

const { FRONTEND_BASE_URL } = getConfig().publicRuntimeConfig;
const dayNamesMap = {
  fri: 'Friday',
  mon: 'Monday',
  sat: 'Saturday',
  sun: 'Sunday',
  thu: 'Thursday',
  tue: 'Tuesday',
  wed: 'Wednesday',
};

export function renderOrganisationDataScript() {
  return (
    <script
      type="application/ld+json"
      data-key="organisation-schema"
      key="organisation-schema"
      dangerouslySetInnerHTML={{
        __html: `
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "url": "${FRONTEND_BASE_URL}",
              "logo": "${FRONTEND_BASE_URL}static/img/logo-chicoree-green-black-backgorund.svg"
            }
          `,
      }}
    />
  );
}

function parseOpeningHours(businessHours) {
  /* eslint-disable no-param-reassign */
  return Object.entries(businessHours).reduce((accum, [day, timeRanges]) => {
    const dayName = dayNamesMap[day];

    if (timeRanges.length) {
      timeRanges.forEach(range => {
        const openTime = range.openTime.slice(0, 5);
        const closeTime = range.closeTime.slice(0, 5);
        const timeRangeKey = `${openTime}-${closeTime}`;

        if (!accum[timeRangeKey]) {
          accum[timeRangeKey] = {
            closeTime,
            dayOfWeek: [],
            openTime,
          };
        }

        accum[timeRangeKey].dayOfWeek.push(dayName);
      });
    }

    if (!timeRanges.length) {
      const openTime = '00:00';
      const closeTime = '00:00';
      const timeRangeKey = `${openTime}-${closeTime}`;

      if (!accum[timeRangeKey]) {
        accum[timeRangeKey] = {
          closeTime,
          dayOfWeek: [],
          openTime,
        };
      }

      accum[timeRangeKey].dayOfWeek.push(dayName);
    }

    return accum;
  }, {});
  /* eslint-enable no-param-reassign */
}

function bodyItemToHtml(bodyItem) {
  if (typeof bodyItem === 'string') {
    return bodyItem.replace(/h4|div/g, 'p');
  }

  if (typeof bodyItem.value === 'string') {
    return bodyItem.value.replace(/h4|div/g, 'p');
  }

  if (isObject(bodyItem.value)) {
    return `
      ${bodyItem.value.intro || bodyItem.value.heading || bodyItem.value.title || ''}
      ${
        Array.isArray(bodyItem.value?.items)
          ? `<ul>${bodyItem.value.items
              .map(
                item => `
       <li>
          ${item?.fact && item?.subFact ? `<p>${item?.fact}</p><p>${item?.subFact}</p>` : ''}
          ${item?.title ? `<p>${item?.title}</p>` : ''}
          ${item?.videoUrl ? `<a href='${item?.videoUrl}'>${item?.linkText}</a>` : ''}
          ${
            item?.videoId
              ? `<a href='https://youtube.com/watch?v=${item?.videoId}'>${item?.linkText}</a>`
              : ''
          }
        </li>`,
              )
              .filter(item => !!item)
              .join('')}</ul>`
          : ''
      }
    `;
  }

  return String(bodyItem.value);
}

function parseAndCleanDescriptionBody(body) {
  return body
    .map(bodyItem => bodyItemToHtml(bodyItem))
    .map(item =>
      item
        .replace(/\n/g, '')
        .replace(/\s\s+/g, ' ')
        .replace(/"/g, '\\"')
        .trim(),
    )
    .join('<br>');
}

export function renderJobDetailsScript({
  lastUpdated = '',
  title = '',
  areaOfOperation = {
    title: '',
  },
  body = [],
  placeOfWork = '',
  workloadFrom = '',
  workloadTo = '',
  description = '',
  jobDescriptionAccordionTitle = '',
  store = {},
}) {
  const hasStoreDetails = store && store.city && store.houseNr && store.zipCode;

  return (
    <script
      type="application/ld+json"
      key="job-details-schema"
      data-key="job-details-schema"
      dangerouslySetInnerHTML={{
        __html: `
          {
            "@context" : "https://schema.org/",
            "@type" : "JobPosting",
            "title" : "${areaOfOperation?.title} ${title || placeOfWork}${
              workloadFrom && workloadTo
                ? ` ${workloadFrom}%-${workloadTo}%`
                : ''
            }",
            "description": "<p>${description}</p><br>${
              body &&
              body?.length > 0 &&
              `<p>${jobDescriptionAccordionTitle}</p><br>${parseAndCleanDescriptionBody(body)}`
            }",
            "identifier": {
              "@type": "PropertyValue",
              "name": "Chicorée Mode AG"
            },
            "datePosted": "${lastUpdated}",
            "employmentType" : "${workloadFrom >= 100 ? 'FULL_TIME' : 'PART_TIME'}",
            "hiringOrganization" : {
              "@type" : "Organization",
              "name" : "Chicorée Mode AG",
              "sameAs" : "https://chicoree.ch"
            },
            "jobLocation": {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "${placeOfWork}",
                "addressCountry": "CH"
                ${
                  hasStoreDetails
                    ? `,
                  "streetAddress": "${store.street} ${store.houseNr}",
                  "postalCode": "${store.zipCode}",
                  "addressRegion": "${store.canton}"
                `
                    : ''
                }
              }
            }
          }
        `,
      }}
    />
  );
}

const getSchemaAvailability = availability => {
  const schemaArray = [];

  if (availability) {
    schemaArray.push('https://schema.org/InStock');
  } else {
    schemaArray.push('https://schema.org/OutOfStock');
  }

  return schemaArray.join('", "');
};

export function renderProductDataScript({
  name,
  images,
  sku,
  brand,
  url,
  priceCurrency,
  price,
  priceValidUntil,
  availability,
}) {
  const isDiscontinued = isAfter(
    startOfDay(getDateZurich()),
    convertToZurichTz(startOfDay(new Date(priceValidUntil))),
  );

  return (
    <script
      type="application/ld+json"
      key="product-schema"
      data-key="product-schema"
      dangerouslySetInnerHTML={{
        __html: `
            {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": "${name}",
      "image": ["${images.join('", "')}"],
      "sku": "${sku}",
      "brand": {
        "@type": "Brand",
        "name": "${brand}"
      },
      "offers": ${JSON.stringify({
        '@type': 'Offer',
        url,
        priceCurrency,
        price: String(price),
        itemCondition: 'https://schema.org/NewCondition',
        ...(!isDiscontinued && {
          priceValidUntil,
          availability: getSchemaAvailability(availability),
        }),
      })}
    }`,
      }}
    />
  );
}

export function renderStoreDataScript(data) {
  const openingHours = parseOpeningHours(data.businessHours);

  const openingHoursEntries = Object.values(openingHours)
    .map(
      item => `
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["${item.dayOfWeek.join('", "')}"],
          "opens": "${item.openTime}",
          "closes": "${item.closeTime}"
        }`,
    )
    .join(',');

  return (
    <script
      type="application/ld+json"
      key="store-schema"
      data-key="store-schema"
      dangerouslySetInnerHTML={{
        __html: `
            {
      "@context": "https://schema.org",
      "@type": "Store",
      "image": [
        "${FRONTEND_BASE_URL}static/img/store-placeholder-1x1.jpg",
        "${FRONTEND_BASE_URL}static/img/store-placeholder-4x3.jpg",
        "${FRONTEND_BASE_URL}static/img/store-placeholder-16x9.jpg"
      ],
      "@id": "http://store-${data.id}.chicoree.ch",
      "name": "Chicorée ${data.title}",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "${data.street} ${data.houseNr}",
        "addressLocality": "${data.city}",
        "postalCode": "${data.zipCode}",
        "addressCountry": "${data.country || 'CH'}"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": ${data.lat},
        "longitude": ${data.lng}
      },
      "url": "${FRONTEND_BASE_URL}${data.urlPath}",
      "priceRange": "$",
      "telephone": "${data.phone}",
      "openingHoursSpecification": [${openingHoursEntries}]
    }
          `,
      }}
    />
  );
}
